import I18n from 'app/i18n';
import {toastError} from 'app/components/toasts/liteToast';
import {orderStore, shopStore} from 'app/store/global';
import {getLinkPath, slowConnectionPromise} from 'app/utils';
import {ajaxError, extractUnprocessableErrorMessage} from 'app/utils/ajaxError';
import {confirmGoToDeposits} from 'app/utils/app_links';
import Get, {type ParamsInterface} from 'app/utils/Get';
import {UNPROCESSABLE_ENTITY} from 'app/utils/HttpStatuses';
import {isRecord} from 'app/utils/isRecord';
import {hideLastPopup} from 'app/utils/modals/ModalsService';
import {modalAlert} from 'app/utils/modals/popups';
import Post from 'app/utils/Post';
import type UserPurchaseInterface from 'app/interfaces/shop/UserPurchaseInterface';

const PURCHASES_PAGE = 1;

class MarketplaceOrderController {
    buy(id: number, query: ParamsInterface): Promise<void> {
        return slowConnectionPromise(this.performBuy(id, query)) as Promise<void>;
    }

    async fetchOrders(marketplaceId: string | undefined, pageParams: ParamsInterface): Promise<boolean> {
        if (!marketplaceId) {
            return false;
        }

        try {
            return await this.loadOrders(marketplaceId, pageParams);
        } catch {
            ajaxError({status: 0});
            return false;
        }
    }

    private async loadOrders(marketplaceId: string, pageParams: ParamsInterface): Promise<boolean> {
        const httpResponse = await new Get({
            params: pageParams,
            url: getLinkPath(`/shop/marketplace/${marketplaceId}/orders`)
        }).execute();
        const payload = await this.parsePayload(httpResponse);

        if (!httpResponse.ok || !payload || !Array.isArray(payload.orders)) {
            ajaxError({responseJSON: payload, status: httpResponse.status});
            return false;
        }

        this.refreshOrders(payload);
        return true;
    }

    private async performBuy(id: number, query: ParamsInterface): Promise<void> {
        try {
            const httpResponse = await new Post({
                params: query,
                url: getLinkPath(`/artifact_orders/${id}/buy`)
            }).execute();
            const payload = await this.parsePayload(httpResponse);

            if (!httpResponse.ok) {
                this.handleError(httpResponse.status, payload);
                return;
            }

            this.applySuccess(payload, httpResponse.status);
        } catch {
            ajaxError({status: 0});
        }
    }

    private applySuccess(payload: Record<string, unknown> | null, status: number): void {
        if (!payload || !Array.isArray(payload.orders)) {
            ajaxError({status});
            return;
        }

        this.refreshOrders(payload);
        shopStore.updatePurchasesData(
            this.purchasesFrom(payload),
            Boolean(payload.has_more),
            PURCHASES_PAGE
        );
    }

    private handleError(status: number, payload: Record<string, unknown> | null): void {
        if (this.isInsufficientFunds(payload)) {
            confirmGoToDeposits();
            return;
        }

        if (!payload) {
            ajaxError({status});
            return;
        }

        this.refreshOrders(payload);
        hideLastPopup();
        this.showPurchaseError(status, payload);
    }

    private showPurchaseError(status: number, payload: Record<string, unknown>): void {
        const message = extractUnprocessableErrorMessage(payload);

        if (status === UNPROCESSABLE_ENTITY && message) {
            toastError(message);
            return;
        }

        modalAlert(I18n.t('there_was_a_problem_purchasing_your_item'));
    }

    private isInsufficientFunds(payload: Record<string, unknown> | null): boolean {
        return extractUnprocessableErrorMessage(payload) === I18n.t('insufficient_funds');
    }

    private refreshOrders(payload: Record<string, unknown>): void {
        if (!Array.isArray(payload.orders)) {
            return;
        }

        orderStore.updateOrders({
            orders: payload.orders,
            paginate: payload.paginate,
            recipe: payload.recipe,
            requested_artifacts_data: payload.requested_artifacts_data
        });
    }

    private purchasesFrom(payload: Record<string, unknown>): UserPurchaseInterface[] {
        return Array.isArray(payload.purchases)
            ? payload.purchases as UserPurchaseInterface[]
            : [];
    }

    private async parsePayload(httpResponse: Response): Promise<Record<string, unknown> | null> {
        try {
            const parsed: unknown = await httpResponse.json();

            return isRecord(parsed) ? parsed : null;
        } catch {
            return null;
        }
    }
}

export const marketplaceOrderController = new MarketplaceOrderController();
